import React from 'react'

const WeeklyRecurrenceSelector = ({ value = [], onChange, error }) => {
  const weekDays = [
    { value: 0, label: 'Sunday', short: 'Sun', color: 'red' },
    { value: 1, label: 'Monday', short: 'Mon', color: 'blue' },
    { value: 2, label: 'Tuesday', short: 'Tue', color: 'green' },
    { value: 3, label: 'Wednesday', short: 'Wed', color: 'yellow' },
    { value: 4, label: 'Thursday', short: 'Thu', color: 'purple' },
    { value: 5, label: 'Friday', short: 'Fri', color: 'pink' },
    { value: 6, label: 'Saturday', short: 'Sat', color: 'indigo' }
  ]

  const handleDayToggle = (dayValue) => {
    const newValue = value.includes(dayValue)
      ? value.filter(day => day !== dayValue)
      : [...value, dayValue].sort((a, b) => a - b)
    
    onChange(newValue)
  }

  const handlePresetSelect = (preset) => {
    switch (preset) {
      case 'weekdays':
        onChange([1, 2, 3, 4, 5]) // Monday to Friday
        break
      case 'weekends':
        onChange([0, 6]) // Saturday and Sunday
        break
      case 'all':
        onChange([0, 1, 2, 3, 4, 5, 6]) // All days
        break
      case 'none':
        onChange([])
        break
      default:
        break
    }
  }

  const getColorClasses = (color, isSelected) => {
    const colorMap = {
      red: isSelected ? 'bg-red-500 text-white border-red-500' : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
      blue: isSelected ? 'bg-blue-500 text-white border-blue-500' : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      green: isSelected ? 'bg-green-500 text-white border-green-500' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
      yellow: isSelected ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
      purple: isSelected ? 'bg-purple-500 text-white border-purple-500' : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
      pink: isSelected ? 'bg-pink-500 text-white border-pink-500' : 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100',
      indigo: isSelected ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
    }
    return colorMap[color] || colorMap.blue
  }

  return (
    <div className="weekly-recurrence-selector">
      <div className="flex items-center justify-between mb-4">
        <label className="block text-sm font-medium text-gray-700">
          Select Days of the Week
        </label>
        
        {/* Quick Presets */}
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">Quick select:</span>
          <button
            type="button"
            onClick={() => handlePresetSelect('weekdays')}
            className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Weekdays
          </button>
          <button
            type="button"
            onClick={() => handlePresetSelect('weekends')}
            className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Weekends
          </button>
          <button
            type="button"
            onClick={() => handlePresetSelect('all')}
            className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            All
          </button>
          <button
            type="button"
            onClick={() => handlePresetSelect('none')}
            className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            None
          </button>
        </div>
      </div>

      {/* Day Selection Grid */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {weekDays.map((day) => {
          const isSelected = value.includes(day.value)
          
          return (
            <button
              key={day.value}
              type="button"
              onClick={() => handleDayToggle(day.value)}
              className={`
                relative p-3 rounded-lg border-2 transition-all duration-200 text-center
                ${getColorClasses(day.color, isSelected)}
                ${isSelected ? 'transform scale-105 shadow-lg' : 'hover:scale-102'}
              `}
            >
              {/* Day abbreviation */}
              <div className="text-sm font-bold mb-1">
                {day.short}
              </div>
              
              {/* Full day name */}
              <div className="text-xs opacity-90">
                {day.label}
              </div>
              
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md">
                  <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Selected Days Summary */}
      {value.length > 0 && (
        <div className="bg-blue-50 rounded-lg p-3 mb-4">
          <div className="flex items-center">
            <div className="text-blue-500 mr-2">📅</div>
            <div className="text-sm text-blue-800">
              <strong>Selected days:</strong>{' '}
              {value
                .sort((a, b) => a - b)
                .map(dayValue => weekDays.find(d => d.value === dayValue)?.label)
                .join(', ')}
            </div>
          </div>
          
          <div className="text-xs text-blue-600 mt-1">
            Template will be available every {value.length === 7 ? 'day' : 
              value.length === 1 ? weekDays.find(d => d.value === value[0])?.label :
              `${value.length} days per week`}
          </div>
        </div>
      )}

      {/* Alternative List View for Mobile */}
      <div className="md:hidden">
        <div className="space-y-2">
          {weekDays.map((day) => {
            const isSelected = value.includes(day.value)
            
            return (
              <label
                key={day.value}
                className="flex items-center p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleDayToggle(day.value)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {day.label}
                </span>
              </label>
            )
          })}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center">
            <div className="text-red-500 mr-2">⚠️</div>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Help text */}
      <div className="mt-4 text-xs text-gray-500">
        💡 Tip: You can select multiple days. The template will be available on all selected days each week.
      </div>
    </div>
  )
}

export { WeeklyRecurrenceSelector }
